import React, { useState } from 'react';
import { 
  X, 
  Download, 
  Copy, 
  Eye, 
  FileText, 
  Image, 
  Volume2, 
  Video, 
  Database,
  Clock,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Maximize2,
  Minimize2,
  BookOpen,
  Lightbulb,
  Target,
  Cpu
} from 'lucide-react';
import { toast } from 'react-toastify';
import AudioControls from './AudioControls';

const NodeOutputPanel = ({ node, result, onClose, isVisible }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeTab, setActiveTab] = useState('output');

  if (!isVisible || !node || !result) return null;

  const getStatusIcon = () => {
    if (result.error) return <XCircle className="w-5 h-5 text-red-500" />;
    if (result.output) return <CheckCircle className="w-5 h-5 text-green-500" />;
    return <AlertTriangle className="w-5 h-5 text-yellow-500" />;
  };

  const getStatusColor = () => {
    if (result.error) return 'border-red-200 bg-red-50';
    if (result.output) return 'border-green-200 bg-green-50';
    return 'border-yellow-200 bg-yellow-50';
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard!', { autoClose: 2000 });
  };

  const downloadFile = (data, filename, mimeType) => {
    const blob = new Blob([data], { type: mimeType });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    toast.success('File downloaded!', { autoClose: 2000 });
  };

  const renderOutput = () => {
    if (result.error) {
      return (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center space-x-2 mb-2">
            <XCircle className="w-5 h-5 text-red-500" />
            <h4 className="font-medium text-red-800">Execution Failed</h4>
          </div>
          <p className="text-red-700 text-sm mb-2">{result.error}</p>
          {result.stack && (
            <details className="text-xs text-red-600">
              <summary className="cursor-pointer hover:text-red-800">Stack trace</summary>
              <pre className="mt-2 p-2 bg-red-100 rounded overflow-x-auto">{result.stack}</pre>
            </details>
          )}
        </div>
      );
    }

    if (!result.output) {
      return (
        <div className="p-4 bg-gray-50 border border-gray-200 rounded-lg">
          <p className="text-gray-600">No output generated</p>
        </div>
      );
    }

    const output = result.output;

    switch (result.type) {
      case 'visual':
        return (
          <div className="space-y-4">
            {output.imageBase64 && (
              <div className="border rounded-lg p-4">
                <div className="flex justify-between items-center mb-2">
                  <h4 className="font-medium">Generated Image</h4>
                  <button
                    onClick={() => downloadFile(
                      atob(output.imageBase64), 
                      output.filename || 'image.jpg',
                      output.mimeType || 'image/jpeg'
                    )}
                    className="p-2 text-blue-600 hover:bg-blue-50 rounded"
                  >
                    <Download className="w-4 h-4" />
                  </button>
                </div>
                <img 
                  src={`data:${output.mimeType || 'image/jpeg'};base64,${output.imageBase64}`}
                  alt="Generated visual"
                  className="max-w-full h-auto rounded border"
                />
                {output.description && (
                  <p className="text-sm text-gray-600 mt-2">{output.description}</p>
                )}
              </div>
            )}
          </div>
        );

      case 'audio':
        return (
          <div className="space-y-4">
            {/* Enhanced Audio Controls */}
            <AudioControls audioData={output} />
            
            {/* Enhanced Script Display */}
            {output.generatedScript && (
              <div className="border rounded-lg p-4">
                <h5 className="font-medium text-sm mb-3 flex items-center">
                  <FileText className="w-4 h-4 mr-2 text-blue-500" />
                  Enhanced Audio Script
                </h5>
                <div className="bg-gray-50 border rounded p-3">
                  <div className="text-sm leading-relaxed max-h-40 overflow-y-auto">
                    {output.generatedScript}
                  </div>
                  <div className="mt-3 pt-2 border-t flex justify-between items-center text-xs text-gray-500">
                    <span>Language: {output.language}</span>
                    <button
                      onClick={() => copyToClipboard(output.generatedScript)}
                      className="text-blue-600 hover:text-blue-800 flex items-center"
                    >
                      <Copy className="w-3 h-3 mr-1" />
                      Copy Script
                    </button>
                  </div>
                </div>
              </div>
            )}
            
            {/* Original Topic Display */}
            {output.originalTopic && output.originalTopic !== output.generatedScript && (
              <div className="border rounded-lg p-4">
                <h5 className="font-medium text-sm mb-2 flex items-center">
                  <Target className="w-4 h-4 mr-2 text-green-500" />
                  Original Topic
                </h5>
                <div className="bg-green-50 border border-green-200 rounded p-2 text-sm">
                  {output.originalTopic}
                </div>
              </div>
            )}
          </div>
        );

      case 'worksheet':
        return (
          <div className="space-y-4">
            <div className="border rounded-lg p-4">
              <h4 className="font-medium mb-3">Generated Worksheets ({output.worksheets?.length || 0})</h4>
              {output.worksheets?.map((worksheet, idx) => (
                <div key={idx} className="border-l-4 border-blue-500 pl-4 py-2 mb-3 bg-blue-50">
                  <div className="flex justify-between items-center">
                    <h5 className="font-medium">Grade {worksheet.grade}</h5>
                    <div className="flex space-x-2">
                      {worksheet.downloadUrl && (
                        <a 
                          href={worksheet.downloadUrl} 
                          download={worksheet.filename}
                          className="p-1 text-blue-600 hover:bg-blue-100 rounded"
                        >
                          <Download className="w-4 h-4" />
                        </a>
                      )}
                      <button
                        onClick={() => copyToClipboard(worksheet.content || '')}
                        className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  {worksheet.content && (
                    <div className="mt-2 text-sm bg-white p-2 rounded max-h-32 overflow-y-auto">
                      <pre className="whitespace-pre-wrap">{worksheet.content.substring(0, 200)}...</pre>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        );

      case 'textGen':
        // Check if it's structured content
        if (output && typeof output === 'object' && output.format === 'structured_educational_content') {
          return (
            <div className="space-y-4">
              {/* Title Section */}
              {output.title && (
                <div className="border rounded-lg p-4 bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200">
                  <h3 className="text-xl font-bold text-blue-900 flex items-center">
                    <BookOpen className="w-6 h-6 mr-2" />
                    {output.title}
                  </h3>
                  <div className="text-sm text-blue-600 mt-1">
                    Grade {output.gradeLevel} • {output.language} • {output.source}
                  </div>
                </div>
              )}

              {/* Introduction */}
              {output.introduction && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium text-green-800 mb-2 flex items-center">
                    <Target className="w-4 h-4 mr-2" />
                    Introduction
                  </h4>
                  <p className="text-sm leading-relaxed">{output.introduction}</p>
                </div>
              )}

              {/* Main Content */}
              {output.mainContent && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium text-gray-800 mb-3">Main Content</h4>
                  <div className="bg-gray-50 border rounded p-3">
                    <p className="text-sm leading-relaxed">{output.mainContent}</p>
                  </div>
                </div>
              )}

              {/* Key Points */}
              {output.keyPoints && Array.isArray(output.keyPoints) && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium text-orange-800 mb-3 flex items-center">
                    <Target className="w-4 h-4 mr-2" />
                    Key Learning Points
                  </h4>
                  <ul className="space-y-2">
                    {output.keyPoints.map((point, idx) => (
                      <li key={idx} className="flex items-start">
                        <span className="inline-block w-6 h-6 bg-orange-500 text-white text-xs rounded-full flex items-center justify-center mt-0.5 mr-3 flex-shrink-0">
                          {idx + 1}
                        </span>
                        <span className="text-sm">{point}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {/* Fun Fact */}
              {output.funFact && (
                <div className="border rounded-lg p-4 bg-yellow-50 border-yellow-200">
                  <h4 className="font-medium text-yellow-800 mb-2 flex items-center">
                    <Lightbulb className="w-4 h-4 mr-2" />
                    Fun Fact!
                  </h4>
                  <p className="text-sm text-yellow-700">{output.funFact}</p>
                </div>
              )}

              {/* Vocabulary */}
              {output.vocabulary && Array.isArray(output.vocabulary) && (
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium text-purple-800 mb-3">Key Vocabulary</h4>
                  <div className="flex flex-wrap gap-2">
                    {output.vocabulary.map((word, idx) => (
                      <span key={idx} className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm">
                        {word}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Conclusion */}
              {output.conclusion && (
                <div className="border rounded-lg p-4 bg-green-50 border-green-200">
                  <h4 className="font-medium text-green-800 mb-2">Conclusion</h4>
                  <p className="text-sm text-green-700">{output.conclusion}</p>
                </div>
              )}

              {/* Action Buttons */}
              <div className="flex justify-end space-x-2 pt-2">
                <button
                  onClick={() => {
                    const fullContent = `${output.title}\n\n${output.introduction}\n\n${output.mainContent}\n\nKey Points:\n${output.keyPoints?.join('\n') || ''}\n\nFun Fact: ${output.funFact}\n\nVocabulary: ${output.vocabulary?.join(', ') || ''}\n\n${output.conclusion}`;
                    copyToClipboard(fullContent);
                  }}
                  className="px-4 py-2 bg-blue-500 text-white text-sm rounded hover:bg-blue-600 flex items-center"
                >
                  <Copy className="w-4 h-4 mr-1" />
                  Copy All Content
                </button>
              </div>
            </div>
          );
        } else {
          // Fallback for non-structured content
          return (
            <div className="border rounded-lg p-4">
              <div className="flex justify-between items-center mb-2">
                <h4 className="font-medium">Generated Content</h4>
                <button
                  onClick={() => copyToClipboard(typeof output === 'string' ? output : JSON.stringify(output, null, 2))}
                  className="p-2 text-gray-600 hover:bg-gray-50 rounded"
                >
                  <Copy className="w-4 h-4" />
                </button>
              </div>
              <div className="bg-gray-50 p-3 rounded text-sm max-h-64 overflow-y-auto">
                <pre className="whitespace-pre-wrap">{typeof output === 'string' ? output : JSON.stringify(output, null, 2)}</pre>
              </div>
            </div>
          );
        }

      case 'api':
        return (
          <div className="border rounded-lg p-4">
            <div className="flex justify-between items-center mb-2">
              <h4 className="font-medium">API Response</h4>
              <button
                onClick={() => copyToClipboard(JSON.stringify(output, null, 2))}
                className="p-2 text-gray-600 hover:bg-gray-50 rounded"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            <div className="bg-gray-50 p-3 rounded text-sm max-h-64 overflow-y-auto">
              <pre className="whitespace-pre-wrap">{
                typeof output === 'string' ? output : JSON.stringify(output, null, 2)
              }</pre>
            </div>
          </div>
        );

      case 'offlineLLM':
        return (
          <div className="border rounded-lg p-4">
            <div className="flex justify-between items-center mb-3">
              <h4 className="font-medium flex items-center">
                <Cpu className="w-4 h-4 mr-2 text-purple-600" />
                Offline LLM Response
              </h4>
              <button
                onClick={() => copyToClipboard(output.generatedText || JSON.stringify(output, null, 2))}
                className="p-2 text-gray-600 hover:bg-gray-50 rounded"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
            
            {output.error ? (
              <div className="bg-red-50 border border-red-200 rounded p-3">
                <p className="text-red-800 font-medium mb-2">Backend API Error</p>
                <p className="text-red-700 text-sm mb-2">{output.message}</p>
                {output.suggestion && (
                  <p className="text-red-600 text-xs">{output.suggestion}</p>
                )}
                <div className="mt-2 text-xs text-red-500">
                  <p>Input: {output.inputText || 'N/A'}</p>
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Generated Text */}
                <div className="bg-gray-50 border rounded p-3">
                  <div className="text-sm leading-relaxed max-h-64 overflow-y-auto">
                    {output.generatedText}
                  </div>
                </div>
                
                {/* Model Information */}
                <div className="flex justify-between items-center text-xs text-gray-500 border-t pt-2">
                  <div className="flex space-x-4">
                    <span>Model: {output.model}</span>
                    {output.tokens && <span>Tokens: {output.tokens}</span>}
                    {output.processingTime && <span>Time: {output.processingTime}</span>}
                  </div>
                  <span className="text-purple-600">Backend Agent</span>
                </div>
                
                {/* API Details */}
                {output.apiUrl && (
                  <div className="text-xs text-gray-400 bg-gray-100 rounded p-2">
                    API: {output.apiUrl}
                  </div>
                )}
              </div>
            )}
          </div>
        );

      default:
        return (
          <div className="border rounded-lg p-4">
            <div className="bg-gray-50 p-3 rounded text-sm max-h-64 overflow-y-auto">
              <pre className="whitespace-pre-wrap">{
                typeof output === 'string' ? output : JSON.stringify(output, null, 2)
              }</pre>
            </div>
          </div>
        );
    }
  };

  const renderMetadata = () => {
    return (
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-4 text-sm">
          <div>
            <span className="font-medium text-gray-700">Execution Time:</span>
            <p className="text-gray-600">{result.timestamp ? new Date(result.timestamp).toLocaleTimeString() : 'N/A'}</p>
          </div>
          <div>
            <span className="font-medium text-gray-700">Node Type:</span>
            <p className="text-gray-600 capitalize">{result.type || 'Unknown'}</p>
          </div>
          {result.metadata && (
            <>
              {result.metadata.processingTime && (
                <div>
                  <span className="font-medium text-gray-700">Processing Time:</span>
                  <p className="text-gray-600">{result.metadata.processingTime}</p>
                </div>
              )}
              {result.metadata.source && (
                <div>
                  <span className="font-medium text-gray-700">Source:</span>
                  <p className="text-gray-600">{result.metadata.source}</p>
                </div>
              )}
            </>
          )}
        </div>
        
        {result.metadata && (
          <div className="border-t pt-3">
            <span className="font-medium text-gray-700 text-sm">Raw Metadata:</span>
            <div className="mt-1 bg-gray-50 p-2 rounded text-xs max-h-32 overflow-y-auto">
              <pre>{JSON.stringify(result.metadata, null, 2)}</pre>
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className={`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 ${isVisible ? '' : 'hidden'}`}>
      <div className={`bg-white rounded-lg shadow-xl transition-all duration-300 ${
        isExpanded ? 'w-full h-full m-4' : 'w-full max-w-4xl max-h-[80vh] m-4'
      } overflow-hidden`}>
        
        {/* Header */}
        <div className={`flex items-center justify-between p-4 border-b ${getStatusColor()}`}>
          <div className="flex items-center space-x-3">
            {getStatusIcon()}
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {node.data.label || `${node.type} node`}
              </h2>
              <p className="text-sm text-gray-600">Node ID: {node.id}</p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-2 text-gray-600 hover:bg-white hover:bg-opacity-50 rounded transition-colors"
            >
              {isExpanded ? <Minimize2 className="w-4 h-4" /> : <Maximize2 className="w-4 h-4" />}
            </button>
            <button
              onClick={onClose}
              className="p-2 text-gray-600 hover:bg-white hover:bg-opacity-50 rounded transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200">
          <div className="flex">
            <button
              onClick={() => setActiveTab('output')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'output'
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Eye className="w-4 h-4 inline mr-2" />
              Output
            </button>
            <button
              onClick={() => setActiveTab('metadata')}
              className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'metadata'
                  ? 'border-blue-500 text-blue-600 bg-blue-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
              }`}
            >
              <Database className="w-4 h-4 inline mr-2" />
              Metadata
            </button>
          </div>
        </div>

        {/* Content */}
        <div className={`p-4 overflow-y-auto ${isExpanded ? 'h-full' : 'max-h-96'}`}>
          {activeTab === 'output' ? renderOutput() : renderMetadata()}
        </div>
      </div>
    </div>
  );
};

export default NodeOutputPanel;